// MessengerPress Phase 2 — bottom panel: job history (issue #2392).
// Lists GeneratedPrintReadyPdf rows globally in pages of 10 and polls every 2s
// while any visible row is in a non-terminal state. Each row exposes a
// Download link to the resolved S3 url once the job is COMPLETE.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import colors from 'utils/styling/colors'
import {
  listMessengerPressJobs,
  type MessengerPressJob,
} from '@/api/messenger-press'

// Inline data-testid constants — kept in lockstep with the central
// registry at apps/mdl-frontend/tests/utils/selectors.ts.
const TID_JOB_HISTORY = 'messenger-press-job-history'
const TID_JOB_ROW_PREFIX = 'messenger-press-job-row-'
const TID_JOB_STATUS = 'messenger-press-job-status'
const TID_JOB_PAGE_PREV = 'messenger-press-job-page-prev'
const TID_JOB_PAGE_NEXT = 'messenger-press-job-page-next'

export type JobHistoryListProps = {
  // Used to invalidate the cache and refresh after a new job is enqueued.
  refreshKey?: number
}

const POLL_INTERVAL_MS = 2_000
const PAGE_SIZE = 10

// Terminal job statuses — when every visible job is in one of these we
// stop polling to avoid hammering the BE forever. Mirrors PdfStatusKey from
// @messenger/types — kept as a literal tuple here so the types stay co-located.
const TERMINAL_STATUSES = ['COMPLETE', 'ERROR', 'TIMEOUT'] as const
type TerminalStatus = (typeof TERMINAL_STATUSES)[number]

const isTerminal = (status: string): status is TerminalStatus =>
  (TERMINAL_STATUSES as readonly string[]).includes(status)

export const JobHistoryList = ({ refreshKey }: JobHistoryListProps) => {
  const [jobs, setJobs] = useState<MessengerPressJob[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const cancelledRef = useRef(false)
  // Monotonically increasing request token — used to ignore stale poll
  // responses so an older in-flight fetch cannot overwrite state produced
  // by a newer one (e.g. slow network + fast poll cadence).
  const latestRequestRef = useRef(0)

  const fetchJobs = useCallback(async (pageToFetch: number) => {
    const requestId = ++latestRequestRef.current
    try {
      // GET /v1/messenger-press?page=&pageSize=
      const response = await listMessengerPressJobs({
        page: pageToFetch,
        pageSize: PAGE_SIZE,
      })
      if (cancelledRef.current || requestId !== latestRequestRef.current) return
      setJobs(response.data.jobs)
      setTotal(response.data.total)
      setError(null)
    } catch (err) {
      if (cancelledRef.current || requestId !== latestRequestRef.current) return
      setError((err as Error).message ?? 'Failed to load job history')
    } finally {
      if (!cancelledRef.current && requestId === latestRequestRef.current) {
        setLoading(false)
      }
    }
  }, [])

  // Parent-driven refresh (a new job was just enqueued) should always
  // snap back to page 1, since the new row is inserted at the head of
  // the createdAt-desc list.
  useEffect(() => {
    setPage(1)
  }, [refreshKey])

  useEffect(() => {
    cancelledRef.current = false
    setLoading(true)
    fetchJobs(page)
    return () => {
      cancelledRef.current = true
    }
  }, [fetchJobs, page, refreshKey])

  // Drive polling: while ANY visible job is in a non-terminal state, poll
  // every POLL_INTERVAL_MS. The COMPLETE / ERROR / TIMEOUT terminal guards
  // make sure we stop polling once everything settles.
  const hasInFlight = useMemo(
    () => jobs.some((job) => !isTerminal(job.status)),
    [jobs],
  )

  useEffect(() => {
    if (!hasInFlight) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }
    if (intervalRef.current) return
    intervalRef.current = setInterval(() => {
      fetchJobs(page)
    }, POLL_INTERVAL_MS)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [hasInFlight, fetchJobs, page])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const canPrev = page > 1
  const canNext = page < totalPages

  return (
    <Panel data-testid={TID_JOB_HISTORY}>
      <PanelHeader>
        <PanelTitle>Job History</PanelTitle>
        <PanelSubtitle>
          Messenger-press jobs (all admins), {PAGE_SIZE} per page. Polls every
          2s while any job is in queue or generating.
        </PanelSubtitle>
      </PanelHeader>

      {loading && <Status>Loading job history…</Status>}
      {error && <ErrorStatus>Error: {error}</ErrorStatus>}
      {!loading && !error && jobs.length === 0 && (
        <Status>No messenger-press jobs yet.</Status>
      )}

      {jobs.length > 0 && (
        <>
          <TableScroller>
            <Table>
              <thead>
                <tr>
                  <Th>Status</Th>
                  <Th>Progress</Th>
                  <Th>Created</Th>
                  <Th>Deceased</Th>
                  <Th>Funeral Home</Th>
                  <Th>Product</Th>
                  <Th>Created By</Th>
                  <Th>Source PDF ID</Th>
                  <Th>Source</Th>
                  <Th>Order Product ID</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  const completed =
                    job.status === 'COMPLETE' && !!job.downloadUrl
                  return (
                    <tr
                      key={job.id}
                      data-testid={`${TID_JOB_ROW_PREFIX}${job.id}`}
                    >
                      <Td>
                        <StatusPill
                          $status={job.status}
                          data-testid={TID_JOB_STATUS}
                        >
                          {job.status}
                        </StatusPill>
                      </Td>
                      <Td>{job.progress}%</Td>
                      <Td>{new Date(job.createdAt).toLocaleString()}</Td>
                      <Td>{job.caseName ?? '—'}</Td>
                      <Td>{job.funeralHomeName ?? '—'}</Td>
                      <Td>{job.productName ?? '—'}</Td>
                      <Td>{job.createdBy ? job.createdBy.slice(0, 8) : '—'}</Td>
                      <Td>
                        <Mono>{job.sourcePdfId ?? '—'}</Mono>
                      </Td>
                      <Td>
                        {job.sourcePdfUrl ? (
                          <ViewLink
                            href={job.sourcePdfUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </ViewLink>
                        ) : (
                          '—'
                        )}
                      </Td>
                      <Td>
                        <Mono>{job.productId ?? '—'}</Mono>
                      </Td>
                      <Td>
                        {completed ? (
                          <DownloadLink
                            href={job.downloadUrl ?? '#'}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Download
                          </DownloadLink>
                        ) : (
                          '—'
                        )}
                      </Td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </TableScroller>
          <PaginationBar>
            <PageInfo>
              Page {page} of {totalPages} · {total} job{total === 1 ? '' : 's'}
            </PageInfo>
            <PageButtons>
              <PageButton
                data-testid={TID_JOB_PAGE_PREV}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!canPrev}
              >
                Prev
              </PageButton>
              <PageButton
                data-testid={TID_JOB_PAGE_NEXT}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={!canNext}
              >
                Next
              </PageButton>
            </PageButtons>
          </PaginationBar>
        </>
      )}
    </Panel>
  )
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${colors('palePrimary')};
  border-radius: 12px;
  padding: 20px;
`

const PanelHeader = styled.div`
  margin-bottom: 4px;
`

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: ${colors('almostBlack')};
`

const PanelSubtitle = styled.p`
  font-size: 13px;
  margin: 0;
  color: ${colors('gray2')};
`

// Horizontal scroll wrapper — the table has many long-form columns
// (full UUIDs, full S3 URLs, joined deceased/funeral-home names) that
// together exceed the panel width. The Panel itself stays non-scrolling
// so the header + pagination controls don't shift on scroll.
const TableScroller = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  background: ${colors('white')};
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${colors('white')};
  border-radius: 8px;
  overflow: hidden;
  /* Force columns to size by content so full-length UUIDs + URLs don't
     wrap inside the row; the parent scroller owns the overflow. */
  table-layout: auto;
  white-space: nowrap;
`

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors('gray2')};
  background: ${colors('paleSecondary')};
`

const Td = styled.td`
  padding: 10px 12px;
  font-size: 13px;
  border-top: 1px solid ${colors('gray4')};
  color: ${colors('almostBlack')};
`

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  font-size: 12px;
`

// Status pill background. The base palette has `error` and `gray3` so those
// flow through the `colors()` helper, but it has no `success` token — admin
// tools in this app (see `MioReport.tsx`) consistently use the literal
// `#059669` (Tailwind `emerald-600`) for success states. Keep them locked in
// a const map so the palette is obvious at a glance and easy to swap if a
// `success` theme token is ever added.
const STATUS_COMPLETE_BG = '#059669'
const StatusPill = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) => {
    if ($status === 'COMPLETE') return STATUS_COMPLETE_BG
    if ($status === 'ERROR' || $status === 'TIMEOUT') return colors('error')
    return colors('gray3')
  }};
  color: white;
`

const DownloadLink = styled.a`
  padding: 6px 10px;
  border-radius: 6px;
  background: ${colors('brand')};
  color: white;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
`

// Compact secondary action — same shape as DownloadLink but visually
// subordinate so the primary Download CTA in the Actions column remains
// the most prominent call-to-action on each row.
const ViewLink = styled.a`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid ${colors('gray4')};
  background: ${colors('white')};
  color: ${colors('almostBlack')};
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    background: ${colors('paleSecondary')};
  }
`

const Status = styled.div`
  padding: 12px;
  font-size: 13px;
  color: ${colors('gray2')};
`

const ErrorStatus = styled(Status)`
  color: ${colors('error')};
`

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`

const PageInfo = styled.div`
  font-size: 12px;
  color: ${colors('gray2')};
`

const PageButtons = styled.div`
  display: flex;
  gap: 8px;
`

const PageButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${colors('gray4')};
  background: ${colors('white')};
  color: ${colors('almostBlack')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`
